import { debugLog } from '../../utils/debugLog';
import { useState, useEffect } from "react";
import { TrackPerformanceDisplay } from "./fundCardHelpers";
import { InfoRow } from "../UI";
import { fmtMoney } from "../../parsers/xmlParser";
import { fetchTrackPerformance, extractFundIds, trackNumFromName } from "../../utils/govApi";

export default function MasluliMTab({ fund, cs }) {
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);

  // Reset when policy changes
  useEffect(() => {
    setPerformanceData(null);
    setPerformanceError(null);
  }, [fund.polNum, fund.tochnit, fund.companyName]);

  // Fetch once per policy on mount
  useEffect(() => {
    if (!performanceData && !loadingPerformance && fund.maslulim?.length > 0) {
      const fetchPerf = async () => {
        setLoadingPerformance(true);
        setPerformanceError(null);
        try {
          const ids = extractFundIds(fund.maslulim, fund.sugMutzar, fund.companyCode);
          debugLog(`[FundCard] ${fund.polNum} (sug=${fund.sugMutzar}): extractedIds=${JSON.stringify(ids)}, companyCode=${fund.companyCode}`);
          if (ids.length > 0) {
            const results = await fetchTrackPerformance(ids, fund.sugMutzar);
            debugLog(`[FundCard] ${fund.polNum}: results keys=${JSON.stringify(Object.keys(results))}`);
            if (Object.keys(results).length === 0) {
              setPerformanceError("לא נמצאו נתונים עדכניים ב-API הממשלתי עבור קודים אלו.");
            }
            setPerformanceData(results);
          } else {
            debugLog("[govApi Debug] No valid IDs found for", fund.polNum);
          }
        } catch (err) {
          setPerformanceError("שגיאה בתקשורת עם ה-API הממשלתי.");
          setPerformanceData({});
        }
        setLoadingPerformance(false);
      };
      fetchPerf();
    }
  }, [fund.polNum]); // eslint-disable-line

  const isHealthInsurance = fund.monthlyPremiumTotal != null;

  // ── Health insurance mode: compact coverage table ─────────────────────────
  if (isHealthInsurance) {
    const maslulim = fund.maslulim || [];
    const totalPremium = maslulim.reduce((s, m) => s + (m.monthlyPremium || 0), 0);
    const hTh = { textAlign: "right", padding: "7px 10px", fontWeight: 700, fontSize: 11, color: "#64748b", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };
    const hTd = { padding: "6px 10px", fontSize: 12, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
    return (
      <>
        <div style={{ padding: "10px 14px 6px", fontWeight: 700, fontSize: 13, color: cs.accent, borderBottom: "1px solid #e2e8f0", marginBottom: 8 }}>
          סה״כ פרמיה חודשית: ₪{totalPremium.toFixed(2)}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={hTh}>מספר כיסוי</th>
                <th style={hTh}>שם כיסוי</th>
                <th style={hTh}>סוג כיסוי</th>
                <th style={hTh}>מבוטח</th>
                <th style={hTh}>ת"ז מבוטח</th>
                <th style={hTh}>שם מבוטח</th>
                <th style={hTh}>ת. תחילת ביטוח</th>
                <th style={hTh}>ת. תום ביטוח</th>
                <th style={hTh}>פרמיה חודשית</th>
                <th style={hTh}>פרמיה שנתית</th>
                <th style={hTh}>סכום ביטוח</th>
              </tr>
            </thead>
            <tbody>
              {maslulim.map((m, i) => (
                <tr key={i}>
                  <td style={{ ...hTd, fontFamily: "monospace", direction: "ltr", textAlign: "right" }}>{m.productCode || "—"}</td>
                  <td style={{ ...hTd, fontWeight: 600, color: "#0f172a", whiteSpace: "normal" }}>{m.name || "כיסוי"}</td>
                  <td style={hTd}>{m.coverageType || "—"}</td>
                  <td style={hTd}>{m.insuredRole || "—"}</td>
                  <td style={{ ...hTd, fontFamily: "monospace", direction: "ltr", textAlign: "right" }}>{m.insuredTaz || "—"}</td>
                  <td style={hTd}>{m.insuredName || "—"}</td>
                  <td style={hTd}>{m.startDate || "—"}</td>
                  <td style={hTd}>{m.endDate || (m.duration ? `${m.duration} חודשים` : "—")}</td>
                  <td style={{ ...hTd, fontWeight: 700, color: cs.accent, direction: "ltr", textAlign: "right" }}>
                    {m.monthlyPremium > 0 ? `₪${m.monthlyPremium.toFixed(2)}` : "—"}
                  </td>
                  <td style={{ ...hTd, direction: "ltr", textAlign: "right" }}>
                    {m.annualPremium > 0 ? `₪${m.annualPremium.toFixed(2)}` : m.monthlyPremium > 0 ? `₪${(m.monthlyPremium * 12).toFixed(2)}` : "—"}
                  </td>
                  <td style={{ ...hTd, direction: "ltr", textAlign: "right" }}>
                    {m.sumInsured > 0 ? `₪${m.sumInsured.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  // ── Managed portfolio mode: horizontal chips ─────────────────────────────
  if (fund.cat === "תיק מנוהל") {
    const items = (fund.maslulim || []).filter(m => !m.isAggregate && (m.balance || 0) > 0);
    const totalBalance = items.reduce((s, m) => s + (m.balance || 0), 0);
    return (
      <>
        <div style={{ padding: "6px 0 10px", fontWeight: 700, fontSize: 13, color: cs.accent, borderBottom: "1px solid #e2e8f0", marginBottom: 12 }}>
          סה״כ צבירה: {fmtMoney(totalBalance)}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {items.map((m, i) => {
            const pct = totalBalance > 0 ? (m.balance / totalBalance * 100).toFixed(1) : null;
            return (
              <div key={i} style={{
                minWidth: 110, flex: "0 0 auto",
                padding: "10px 14px", borderRadius: 10,
                background: "#f8fafc", border: `1px solid ${cs.border}55`,
                textAlign: "center", display: "flex", flexDirection: "column", gap: 4,
              }}>
                <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.3, minHeight: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {m.name}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: cs.accent }}>
                  {fmtMoney(m.balance)}
                </div>
                {pct && (
                  <div style={{ fontSize: 11, color: "#94a3b8", background: "#e2e8f0", borderRadius: 8, padding: "1px 6px" }}>
                    {pct}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // ── Savings / pension mode: original track breakdown ─────────────────────
  const totalBalance = (fund.maslulim || []).reduce((s, m) => s + (m.balance || 0), 0);
  const groupedMaslulim = Object.values(
    (fund.maslulim || []).reduce((acc, m) => {
      const key = m.name || "—";
      if (!acc[key]) acc[key] = { ...m, balance: m.balance || 0 };
      else acc[key].balance = (acc[key].balance || 0) + (m.balance || 0);
      return acc;
    }, {})
  );

  return (
    <>
      <div style={{ padding: "10px 14px 6px", fontWeight: 700, fontSize: 13, color: cs.accent, borderBottom: "1px solid #e2e8f0", marginBottom: 10 }}>
        סה״כ צבירה במסלולים: {fmtMoney(totalBalance)}
      </div>
      {groupedMaslulim.map((m, i) => {
        const sharePct = totalBalance > 0 ? (m.balance / totalBalance * 100).toFixed(1) : null;

        let displayName = m.name || "—";
        displayName = displayName.replace(/^\d+/, "").trim();
        if (m.code && m.code.length >= 7) {
          const codeNum = parseInt(m.code.slice(-7), 10);
          if (!isNaN(codeNum)) {
            const suffix = `- ${codeNum}`;
            if (!displayName.endsWith(suffix)) {
              displayName = `${displayName} ${suffix}`;
            }
          }
        }

        const codeStr = String(m.code || "");
        let normCode = null;
        const sm = String(fund.sugMutzar);
        if (sm === "1" || sm === "5") {
          let trackNum = (codeStr.length >= 7)
            ? parseInt(codeStr.slice(-7), 10)
            : parseInt(m.trackId || codeStr, 10);
          if (isNaN(trackNum) || trackNum === 0) trackNum = trackNumFromName(m.name);
          normCode = (!isNaN(trackNum) && trackNum !== 0) ? String(trackNum) : null;
        } else {
          let cleanId = (codeStr.length >= 7) ? String(parseInt(codeStr.slice(-7), 10)) : (m.trackId || codeStr);
          if (!cleanId || cleanId === "0") {
            const fromName = trackNumFromName(m.name);
            cleanId = isNaN(fromName) ? null : String(fromName);
          }
          normCode = (cleanId && cleanId !== "0") ? cleanId : null;
        }

        return (
          <div key={i} style={{ marginBottom: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: cs.accent }}>
                {displayName}
              </div>
              {sharePct && <div style={{ fontSize: 11, color: "#64748b", background: "#e2e8f0", borderRadius: 10, padding: "1px 8px" }}>{sharePct}% מהצבירה</div>}
            </div>
            {m.balance > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#64748b" }}>צבירה במסלול</span>
                <span style={{ fontWeight: 700 }}>{fmtMoney(m.balance)}</span>
              </div>
            )}
            {m.tsua != null && m.tsua !== 0 && <InfoRow label="תשואה נטו" value={m.tsua} pct />}

            {loadingPerformance && i === 0 && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, fontStyle: "italic" }}>טוען נתוני ביצועים מה-API...</div>
            )}
            {performanceError && i === 0 && (
              <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 8 }}>{performanceError}</div>
            )}
            {performanceData && normCode && performanceData[normCode] && (
              <TrackPerformanceDisplay data={performanceData[normCode]} color={cs.accent} />
            )}
          </div>
        );
      })}
    </>
  );
}
